import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '../../lib/utils';

export interface FilterSelectOption {
  value: string;
  label: string;
  /** Tailwind bg-* class for the leading status dot; omit for no dot. */
  dot?: string;
}

interface FilterSelectProps {
  label: string;
  options: FilterSelectOption[];
  value: string;
  onChange: (next: string) => void;
  /** Width of the dropdown panel. */
  contentClassName?: string;
  className?: string;
}

/**
 * Single-value sibling of MultiSelect, sharing the exact same trigger shape.
 *
 * Exists so single-choice filters (Status, item state) don't fall back to a
 * bare `<select>` — the Confluence review flagged filter controls coming in
 * inconsistent sizes and styles as a main source of visual noise.
 */
export function FilterSelect({
  label,
  options,
  value,
  onChange,
  contentClassName,
  className,
}: FilterSelectProps) {
  const [open, setOpen] = useState(false);
  const active = options.find(o => o.value === value) ?? options[0];
  const isDefault = active?.value === options[0]?.value;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex min-h-8 min-w-[160px] select-none items-center gap-1.5 rounded-md border border-border bg-background py-1 pl-2.5 pr-2 text-left',
            'hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            className,
          )}
        >
          <span className="shrink-0 text-[13px] text-muted-foreground">{label}</span>
          <span
            className={cn(
              'flex flex-1 items-center gap-1.5 text-[13px]',
              isDefault ? 'text-zinc-400' : 'text-foreground',
            )}
          >
            {active?.dot && <span className={cn('h-1.5 w-1.5 rounded-full', active.dot)} />}
            {active?.label}
          </span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
        </button>
      </PopoverTrigger>

      <PopoverContent className={cn('w-[180px] p-1', contentClassName)}>
        {options.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              onChange(option.value);
              setOpen(false);
            }}
            className="flex h-[30px] w-full cursor-pointer items-center gap-2 rounded-sm px-2 text-left text-[13px] hover:bg-accent"
          >
            {option.dot && <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', option.dot)} />}
            <span className="flex-1 truncate">{option.label}</span>
            {option.value === value && <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
